import React, { createContext, useCallback, useContext, useMemo, useState, ReactNode } from 'react';

interface VideoContextType {
  startVideo: () => void;
  setVideoFunction: (videoFn: () => void) => void;
}

const VideoContext = createContext<VideoContextType | undefined>(undefined);

export const useGlobalVideo = () => {
  const context = useContext(VideoContext);
  if (context === undefined) {
    throw new Error('useGlobalVideo must be used within a VideoProvider');
  }
  return context;
};

interface VideoProviderProps {
  children: ReactNode;
}

export const GlobalVideoProvider: React.FC<VideoProviderProps> = ({ children }) => {
  const [videoFunction, setVideoFunction] = useState<(() => void) | null>(null);

  const startVideo = useCallback(() => {
    if (videoFunction) {
      videoFunction();
    }
  }, [videoFunction]);

  const setVideoFn = useCallback((videoFn: () => void) => {
    setVideoFunction(() => videoFn);
  }, []);

  const value = useMemo(
    () => ({ startVideo, setVideoFunction: setVideoFn }),
    [startVideo, setVideoFn],
  );

  return <VideoContext.Provider value={value}>{children}</VideoContext.Provider>;
};
